"use client"

import { useState, useCallback } from "react"
import { useAuth } from "../context/AuthContext"
import type {
  UserDetails,
  ApiUserDetails,
  UserDetailsApiResponse,
  UpdateUserDetailsRequest,
  UpdateUserDetailsResponse,
  LoadingStates,
} from "../types/account-details"
import { ACCOUNT_CONSTANTS } from "../constants/account-details"

export const useUserDetailsApi = () => {
  const { getUserDetails, updateUserDetails } = useAuth()

  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    fetchingUser: false,
    updatingDetails: false,
    updatingPhoto: false,
    changingPassword: false,
  })

  // Transform API response to frontend format
  const transformApiToFrontend = useCallback((apiData: ApiUserDetails): UserDetails => {
    return {
      firstName: apiData.first_name || "",
      lastName: apiData.last_name || "",
      email: apiData.email || "",
      companyName: apiData.company || "",
      companySize: apiData.company_size || "",
      industry: apiData.industry || "",
      country: apiData.country || "",
      state: apiData.state || "",
      city: apiData.city || "",
    }
  }, [])

  // Transform frontend data to API format
  const transformFrontendToApi = useCallback((frontendData: Partial<UserDetails>): UpdateUserDetailsRequest => {
    const apiData: UpdateUserDetailsRequest = {}

    if (frontendData.firstName !== undefined) apiData.first_name = frontendData.firstName
    if (frontendData.lastName !== undefined) apiData.last_name = frontendData.lastName
    if (frontendData.email !== undefined) apiData.email = frontendData.email
    if (frontendData.companyName !== undefined) apiData.company = frontendData.companyName
    if (frontendData.companySize !== undefined) apiData.company_size = frontendData.companySize
    if (frontendData.industry !== undefined) apiData.industry = frontendData.industry
    if (frontendData.country !== undefined) apiData.country = frontendData.country
    if (frontendData.state !== undefined) apiData.state = frontendData.state
    if (frontendData.city !== undefined) apiData.city = frontendData.city

    return apiData
  }, [])

  // Fetch user details from API
  const fetchUserDetails = useCallback(async (): Promise<UserDetails | null> => {
    setLoadingStates((prev) => ({ ...prev, fetchingUser: true }))

    try {
      console.log("Fetching user details...")

      const response: UserDetailsApiResponse = await getUserDetails()

      if (response.status === 200 && response.data) {
        const transformedData = transformApiToFrontend(response.data.profile)
        return transformedData
      } else {
        throw new Error(response.message || "Failed to fetch user details")
      }
    } catch (error: any) {
      console.error("Error fetching user details:", error)
      throw new Error(error.userMessage || error.message || ACCOUNT_CONSTANTS.ERROR_MESSAGES.USER_FETCH_FAILED)
    } finally {
      setLoadingStates((prev) => ({ ...prev, fetchingUser: false }))
    }
  }, [getUserDetails, transformApiToFrontend])

  // Update user details via API
  const updateUserDetailsApi = useCallback(
    async (changedData: Partial<UserDetails>): Promise<boolean> => {
      setLoadingStates((prev) => ({ ...prev, updatingDetails: true }))

      try {
        const apiPayload = transformFrontendToApi(changedData)
        const response: UpdateUserDetailsResponse = await updateUserDetails(apiPayload)

        if (response.status === 200) {
          return true
        } else {
          throw new Error(response.message || "Failed to update user details")
        }
      } catch (error: any) {
        console.error("Error updating user details:", error)
        throw new Error(error.userMessage || error.message || ACCOUNT_CONSTANTS.ERROR_MESSAGES.DETAILS_UPDATE_FAILED)
      } finally {
        setLoadingStates((prev) => ({ ...prev, updatingDetails: false }))
      }
    },
    [updateUserDetails, transformFrontendToApi],
  )

  return {
    loadingStates,
    fetchUserDetails,
    updateUserDetailsApi,
    setLoadingStates,
  }
}
